/**
 * Net Assets composition chart: ONE stacked bar per month, exactly two
 * segments -- Fixed Net Assets and Current Net Assets -- summing to the
 * total net assets figure for that month. No separate Liabilities bar, no
 * Net Assets line overlay: liabilities are netted INTO the two buckets on
 * the backend (services/net_assets_series.py) instead of drawn beside them.
 * The SINGLE shared chart component for all five surfaces that render a Net
 * Assets chart (Overall Finance, All Entities, All Personal, single-entity
 * dashboard, and the Assets & Liabilities page for both Entities and
 * Personal scopes).
 *
 * See lib/netAssetsChart.js for the data model: fixedNet/currentNet/net are
 * all backend-computed, never re-derived client-side. Either segment can be
 * negative (a mortgage exceeding its property's valuation makes fixedNet
 * negative; current liabilities exceeding cash makes currentNet negative)
 * -- Recharts stacks a negative-value bar below the axis for the same
 * stackId, and ForecastBarShape (lib/forecastChartUtils.jsx) already
 * normalises the negative-height SVG rect that produces.
 *
 * Dashboards (1-4) pass calendar-year + 3-month-forecast data (forecast
 * rows carry `_isForecast: true`); the Assets & Liabilities page (5) passes
 * plain calendar-year actuals with no forecast -- both render through this
 * same component, `hasForecast` is derived from the data itself.
 */
import {
  ResponsiveContainer, ComposedChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, LabelList,
} from 'recharts';
import { BarChart3, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { formatCompact, formatCurrencyFull } from '../../lib/chartFormatters';
import { ForecastBarShape, ForecastCaveat } from '../../lib/forecastChartUtils';
import { NET_ASSETS_BASIS_CAPTION, stackTotalLabel } from '../../lib/netAssetsChart';

// Distinct from the Bank Balance chart's blue (#2563EB) and the Cash
// Development chart's blue/red/amber trio (UK/FR/Personal), so the two
// full-width charts stacked on the same dashboard never read as the same
// series by colour alone.
const COLORS = {
  fixedNet: '#0F766E',   // teal-700
  currentNet: '#2DD4BF', // teal-400 -- same hue family as fixedNet: both read as "net assets"
};

// Above this many chart points, per-bar labelling is unreadable; only the
// stack-total label keeps showing (on the latest point only). Same
// threshold and rationale as lib/entityBankBalanceHistoryChart.js's
// LABEL_ALL_THRESHOLD.
const LABEL_ALL_THRESHOLD = 20;

/**
 * @param {object[]} chartData - rows from buildNetAssetsChartData / a plain
 *   series mapped through compositionFieldsFromSeriesPoint: { month,
 *   fixedNet, currentNet, net, _isForecast, _source }
 * @param {string} displayCurrency
 * @param {string} [basisCaption] - defaults to the dashboards' static caption;
 *   the A&L page passes the endpoint's own dynamic `basis` string instead.
 * @param {boolean} [isLegacyFallback] - true only when the caller fell back to
 *   the pre-composition-split getMonthlySeries endpoint (net-assets-series
 *   itself failed) -- that source excludes live bank cash from every
 *   historical point, so a caveat is shown.
 */
export function NetAssetsChart({ chartData, displayCurrency, basisCaption, isLegacyFallback }) {
  if (!chartData || chartData.length === 0) return null;

  const hasForecast = chartData.some((r) => r._isForecast);
  const labelAll = chartData.length <= LABEL_ALL_THRESHOLD;
  const lastIndex = chartData.length - 1;
  const shouldLabelTotal = (index) => labelAll || index === lastIndex;

  const hasHistoricalComputed = isLegacyFallback && chartData.some(
    (d) => d._source === 'computed' && !d._isForecast,
  );
  // source="partial" (services/net_assets_series.py): some but not all of
  // today's live in-scope accounts had a cash snapshot that month -- the
  // total is real, never fabricated, but understated relative to today's
  // full account roster. Flagged the same way the Cash Development chart's
  // hollow marker flags its own per-jurisdiction partial months.
  const hasPartialCoverage = chartData.some((r) => r._partial);

  return (
    <Card data-testid="al-net-assets-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-primary" />
          Net Assets
        </CardTitle>
        <CardDescription className="text-xs">{basisCaption || NET_ASSETS_BASIS_CAPTION}</CardDescription>
        <ForecastCaveat show={hasForecast} />
        {!labelAll && (
          <p className="text-[10px] text-muted-foreground">
            Too many months to label every point -- showing the latest value only.
          </p>
        )}
        {hasPartialCoverage && (
          <p className="text-xs text-amber-600 mt-0.5" data-testid="al-net-assets-partial-note">
            Some months include only partial cash coverage -- one or more accounts had not yet started their
            transaction history, so the figure is real but understated. Hover a bar for detail.
          </p>
        )}
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={chartData} margin={{ top: 24, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={(v) => formatCompact(v, displayCurrency)} tick={{ fontSize: 11 }} width={60} />
            <Tooltip
              formatter={(val, name, entry) => {
                const row = entry && entry.payload;
                // Partial coverage only ever affects CASH (part of current
                // net) -- fixed net comes from property valuations/loans,
                // which have their own independent, unrelated "excluded
                // before its dated entry" rule. Flagging the fixed segment
                // too would misattribute the gap to a component it never
                // affects.
                if (!row || !row._partial || name !== 'Current Net Assets') {
                  return [formatCurrencyFull(val, displayCurrency), name];
                }
                const excluded = row._excludedAccounts || [];
                const shown = excluded.slice(0, 3).join(', ');
                const more = excluded.length > 3 ? ` +${excluded.length - 3} more` : '';
                const suffix = excluded.length ? ` (partial -- excludes ${shown}${more})` : ' (partial)';
                return [formatCurrencyFull(val, displayCurrency) + suffix, name];
              }}
              contentStyle={{ fontSize: 12 }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="fixedNet" name="Fixed Net Assets" stackId="netAssets" fill={COLORS.fixedNet}
              shape={<ForecastBarShape data={chartData} />}
            />
            <Bar dataKey="currentNet" name="Current Net Assets" stackId="netAssets" fill={COLORS.currentNet}
              shape={<ForecastBarShape data={chartData} />}>
              <LabelList content={({ x, y, width, height, index }) => {
                if (!shouldLabelTotal(index)) return null;
                const total = stackTotalLabel(chartData[index]);
                if (total == null) return null;
                // Stack-total label sits just outside the TOP edge of this
                // bar's own drawn rectangle -- above it for a positive
                // segment, below it for a negative one (height < 0, Recharts'
                // bottom-edge convention -- see ForecastBarShape's own
                // comment on this). Reads the row by index for the
                // cross-segment total rather than this Bar's own dataKey.
                const labelY = height < 0 ? y + height - 5 : y - 5;
                return (
                  <text x={x + width / 2} y={labelY} textAnchor="middle" fontSize={9} fontWeight={700} fill="hsl(var(--foreground))">
                    {formatCompact(total, displayCurrency)}
                  </text>
                );
              }} />
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
        {hasHistoricalComputed && (
          <div className="flex items-center gap-1.5 mt-2 text-xs text-amber-600 dark:text-amber-400" data-testid="al-chart-cash-note">
            <AlertTriangle className="h-3 w-3" />
            Historical months exclude live bank cash. Use Snapshot Now to capture current month including cash.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
